import * as React from 'react'

import { type Icon, Text, Title } from '@factorialco/deprecated-design-system'
import groupBy from 'lodash/groupBy'

import BoxList from 'modules/core/components/BoxList'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import type Leave from 'modules/timeoff/store/models/Leave'

import { Medical, Other } from './Item'
import MedicalLeaveTooltip from './MedicalLeaveTooltip'
import PastLeaves from './PastLeaves'

const illustrations: Record<string, React.ComponentProps<typeof Icon.Large>['icon']> = {
  past: 'calendar',
  pending_review: 'holidays-event-go',
  rejected: 'holidays-event-cancel',
  medical: 'holidays-event-date',
  current: 'holidays-event-date',
  upcoming: 'holidays-event-date',
}

export type LeaveSectionType = 'medical' | 'upcoming' | 'past' | 'rejected' | 'pending_review'

interface Props {
  employeeId: number
  employeePreferredName: string | null | undefined
  employeeCanApproveLeaves: boolean
  employeeCanUpdateLeaves: boolean
  employeeCanManageLeaveDocuments: boolean
  employeeTimeoffPolicyId: number | null | undefined
  leaves: Leave[]
  type: LeaveSectionType
  year: number
}

function description(
  type: LeaveSectionType,
  employeePreferredName: string | null | undefined,
  employeeCanApproveLeaves: boolean,
  isCurrentEmployee: boolean
) {
  const params = {
    name: employeePreferredName,
  }

  if (employeeCanApproveLeaves) {
    switch (type) {
      case 'medical':
        return i18n.tCurrent('time_off.medical.subtitle.admin', isCurrentEmployee, params)
      case 'upcoming':
        return i18n.tCurrent('time_off.upcoming.subtitle.admin', isCurrentEmployee, params)
      case 'past':
        return i18n.tCurrent('time_off.past.subtitle.admin', isCurrentEmployee, params)
      case 'rejected':
        return i18n.tCurrent('time_off.rejected.subtitle.admin', isCurrentEmployee, params)
      case 'pending_review':
        return i18n.tCurrent('time_off.pending_review.subtitle.admin', isCurrentEmployee, params)
      default:
        throw new Error(`Unknown leave section type: ${type}`)
    }
  } else {
    switch (type) {
      case 'medical':
        return i18n.t('time_off.medical.subtitle.basic.description', params)
      case 'upcoming':
        return i18n.t('time_off.upcoming.subtitle.basic.description', params)
      case 'past':
        return i18n.t('time_off.past.subtitle.basic.description', params)
      case 'rejected':
        return i18n.t('time_off.rejected.subtitle.basic.description', params)
      case 'pending_review':
        return i18n.t('time_off.pending_review.subtitle.basic.description', params)
      default:
        throw new Error(`Unknown leave section type: ${type}`)
    }
  }
}

function title(type: LeaveSectionType) {
  switch (type) {
    case 'medical':
      return i18n.t('time_off.medical.title')

    case 'upcoming':
      return i18n.t('time_off.upcoming.title')

    case 'past':
      return i18n.t('time_off.past.title')

    case 'rejected':
      return i18n.t('time_off.rejected.title')

    case 'pending_review':
      return i18n.t('time_off.pending_review.title')

    default:
      throw new Error(`Unknown leave section type: ${type}`)
  }
}

function renderItem(
  leave: Leave,
  employeeId: number,
  isCurrentEmployee: boolean,
  employeeTimeoffPolicyId: number | null | undefined
) {
  const medical = leave.leaveType?.isMedicalLeave
  const isLegacyMedical = !!leave.attributes.medical_leave_id
  const Component = isLegacyMedical ? Medical : Other

  return (
    <div
      data-testid="leave-item"
      key={leave.attributes.id}
      style={{
        width: '100%',
        ...(medical && {
          marginBottom: '20px',
        }),
      }}
    >
      <Component
        leave={leave}
        employeeId={employeeId}
        isCurrentEmployee={isCurrentEmployee}
        employeeTimeoffPolicyId={employeeTimeoffPolicyId}
      />
    </div>
  )
}

const Section = ({
  employeeId,
  employeePreferredName,
  employeeCanApproveLeaves,
  employeeCanUpdateLeaves,
  employeeCanManageLeaveDocuments,
  employeeTimeoffPolicyId,
  type,
  year,
  leaves,
}: Props) => {
  const isPast = type === 'past'
  const hasLeaves = leaves.length !== 0
  const groups = groupBy(leaves, (leave: Leave) => {
    const isMedical = leave.leaveType?.isMedicalLeave

    if (isMedical) return 'medical'

    if (isPast) return 'past'

    return 'other'
  })
  const illustration = illustrations[type]

  const { employee: currentEmployee } = useCurrentSession()

  const isCurrentEmployee = currentEmployee?.id === employeeId

  return (
    <>
      <Title
        variant="500"
        illustration={illustration}
        title={title(type)}
        description={description(
          type,
          employeePreferredName,
          employeeCanApproveLeaves,
          isCurrentEmployee
        )}
      />
      {groups.medical &&
        groups.medical.map((leave) => (
          <React.Fragment key={leave.attributes.id}>
            {(employeeCanUpdateLeaves || employeeCanManageLeaveDocuments) && (
              <MedicalLeaveTooltip
                key={`${leave.attributes.id}-tooltip`}
                leave={leave}
                employeeId={employeeId}
              />
            )}
            {renderItem(leave, employeeId, isCurrentEmployee, employeeTimeoffPolicyId)}
          </React.Fragment>
        ))}
      {isPast && (
        <>
          {!!groups.past && (
            <BoxList>
              {groups.past.map((leave) =>
                renderItem(leave, employeeId, isCurrentEmployee, employeeTimeoffPolicyId)
              )}
            </BoxList>
          )}
          {!hasLeaves && (
            <Text.Normal>{i18n.t('time_off.past.no_past_absences_current_year')}</Text.Normal>
          )}
          <PastLeaves
            employeeId={employeeId}
            year={year}
            employeeTimeoffPolicyId={employeeTimeoffPolicyId}
          />
        </>
      )}
      {!!groups.other && (
        <BoxList>
          {groups.other.map((leave) =>
            renderItem(leave, employeeId, isCurrentEmployee, employeeTimeoffPolicyId)
          )}
        </BoxList>
      )}
    </>
  )
}

export default Section
